import { companyAssets } from '../../data/companies'
import { ICompany, ICompanyAssets } from '../../types'
import { IWhyWeInvestResponseDTO } from '../contracts/why-we-invest'

const normalize = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '')

interface IResolvedAsset extends ICompanyAssets {
  readonly key: string
}

// Index the local assets by the normalized form of their key for name lookup.
const assetsByNormalizedKey: Record<string, IResolvedAsset> = Object.entries(
  companyAssets,
).reduce<Record<string, IResolvedAsset>>((acc, [key, value]) => {
  acc[normalize(key)] = { key, image: value.image, logo: value.logo }
  return acc
}, {})

// Normalized API company names whose value can't be derived from the asset key
// by prefix — punctuation/abbreviation ("Humans&") or an upstream typo
// ("Physicall Intelligence" with a double "l").
const ASSET_KEY_ALIASES: Record<string, string> = {
  humans: 'humans-and',
  physicallintelligence: 'physical-intelligence',
}

// Keep customer-approved launch content available if the synchronized
// workbook temporarily omits the value. API data still wins whenever it
// contains a usable URL.
const FEATURED_VIDEO_FALLBACKS: Record<string, string> = {
  sequence: 'https://www.youtube.com/watch?v=TCpRwJBQvW0',
}

const usableOptionalValue = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim()
  return trimmed && trimmed.toLowerCase() !== 'n/a' ? trimmed : undefined
}

const resolveAsset = (companyName: string): IResolvedAsset | undefined => {
  const normalized = normalize(companyName)

  // 1. Exact normalized match (e.g. "Groq" -> "groq").
  const exact = assetsByNormalizedKey[normalized]
  if (exact) {
    return exact
  }

  // 2. Explicit alias.
  const aliasKey = ASSET_KEY_ALIASES[normalized]
  if (aliasKey) {
    const aliased = companyAssets[aliasKey]
    return aliased
      ? { key: aliasKey, image: aliased.image, logo: aliased.logo }
      : undefined
  }

  // 3. Longest asset key that is a prefix of the name. The API uses fuller
  //    names than our short keys, e.g. "Arena AI" -> "arena",
  //    "Hone Health" -> "hone", "Mistral AI" -> "mistral".
  let best: IResolvedAsset | undefined
  let bestLength = 0
  for (const [normalizedKey, asset] of Object.entries(assetsByNormalizedKey)) {
    if (
      normalizedKey.length > bestLength &&
      normalized.startsWith(normalizedKey)
    ) {
      best = asset
      bestLength = normalizedKey.length
    }
  }
  return best
}

const toThesis = (thesis: string | undefined): readonly string[] => {
  if (!thesis) {
    return []
  }
  return thesis
    .split(/\n\s*\n/)
    .map(paragraph => paragraph.trim())
    .filter(paragraph => paragraph.length > 0)
}

export const mapWhyWeInvestCompanies = (
  response: IWhyWeInvestResponseDTO,
): ICompany[] => {
  const items =
    (response as IWhyWeInvestResponseDTO | null | undefined)?.data ?? []

  return items.reduce<ICompany[]>((acc, item) => {
    if (!item.company_name) {
      return acc
    }
    const asset = resolveAsset(item.company_name)
    // Skip companies that have no matching local asset (images stay local).
    if (!asset) {
      return acc
    }

    acc.push({
      id: asset.key,
      name: item.company_name,
      image: asset.image,
      logo: asset.logo,
      description: item.description ?? '',
      investmentThesis: toThesis(item.investment_thesis),
      featuredVideo:
        usableOptionalValue(item.featured_video) ??
        FEATURED_VIDEO_FALLBACKS[asset.key],
      links: {
        x: item.x,
        linkedin: item.linkedin,
        website: item.website,
      },
    })
    return acc
  }, [])
}
